/**
 * Strata (mai-app) 与 LO 共用父域 Cookie 时的约定（与 src/config/common TOKEN_KEY、AccountAuth 一致）：
 * - 统一认证 key：`ZeitroA`。
 *
 * LO 只认 sessionStorage「ZeitroA」。
 *
 * - 首次：从 Cookie 写入 sessionStorage。
 * - Cookie 优先：只要 Cookie 中有有效 JWT，就覆盖 sessionStorage「ZeitroA」。
 * - 若 Cookie 不存在但 LO sessionStorage「ZeitroA」有效，则把 session 同步回 Cookie，保持 Strata 状态。
 * - 写入 Cookie 同步的 JWT 时：同步写入与 /los/signin 一致的 state / originalstate，并 GET /los/bootstrapsession 校验（当前 State 与 signin 相同为空串）。
 * - 已过期的 JWT 不会写入 session（避免首页仍带过期 Bearer 调 /los/getloanestimatedclosingfee 触发 401 弹窗）。
 * - LO 登录成功后应调用 syncLoTokenToStrataCookies：清旧 Strata 认证 Cookie 后按普通租户名写回 Strata，实现 POS→Strata；若生产环境该 Cookie 仅服务端 HttpOnly 下发，则前端无法覆盖，需单独接口 Set-Cookie。
 */
var STRATA_JWT_COOKIE_KEY = "ZeitroA";
function findStrataJwtFromDocumentCookie() {
    if (!document.cookie) {
        return null;
    }
    var segments = document.cookie.split(";");
    for (var i = 0; i < segments.length; i++) {
        var segment = segments[i].trim();
        if (!segment) {
            continue;
        }
        var eq = segment.indexOf("=");
        if (eq <= 0) {
            continue;
        }
        var cname = segment.slice(0, eq).trim();
        var cval = segment.slice(eq + 1);
        var isStrataAuthName = cname === STRATA_JWT_COOKIE_KEY;
        if (!isStrataAuthName) {
            continue;
        }
        var token = cval;
        try {
            token = decodeURIComponent(token);
        } catch (e) {
            token = cval;
        }
        token = (token || "").trim();
        if (token.split(".").length >= 3) {
            return token;
        }
    }
    return null;
}

function parseJwtPayload(token) {
    try {
        var part = token.split(".")[1];
        if (!part) {
            return null;
        }
        var b64 = part.replace(/-/g, "+").replace(/_/g, "/");
        var pad = b64.length % 4;
        if (pad) {
            b64 += "====".slice(0, 4 - pad);
        }
        var json = atob(b64);
        return JSON.parse(json);
    } catch (e) {
        return null;
    }
}

function normalizeCookieNameForClear(cname) {
    try {
        return decodeURIComponent(cname.trim());
    } catch (e) {
        return cname.trim();
    }
}

function isStrataZeitroAuthCookieName(cname) {
    var n = normalizeCookieNameForClear(cname);
    if (n === STRATA_JWT_COOKIE_KEY || n === "Zeitro A") {
        return true;
    }
    // Clean up legacy customer-prefixed Strata cookies that LO no longer writes or reads.
    return n.endsWith("_Zeitro A");
}

function getLoCookieParentDomain() {
    var hostname = window.location.hostname || "";
    var parts = hostname.split(".");
    if (parts.length > 1) {
        return "." + parts.slice(-2).join(".");
    }
    return hostname;
}

/**
 * 与 mai-app `Cookies.set` 一致：path=/、父域、约 7 天。
 */
function setParentDomainJwtCookie(rawCookieName, token, domain) {
    if (typeof document === "undefined" || !token) {
        return;
    }
    var maxAgeSec = 7 * 24 * 60 * 60;
    var encName = encodeURIComponent(rawCookieName);
    var encVal = encodeURIComponent(token);
    document.cookie =
        encName +
        "=" +
        encVal +
        ";path=/;max-age=" +
        maxAgeSec +
        ";domain=" +
        domain;
}

/**
 * 获取当前有效的认证 token（优先 Cookie，其次 sessionStorage）
 * 用于 LoginPage 等需要判断用户是否已登录的场景
 */
export function getValidAuthToken() {
    // 优先从 Cookie 获取（Strata 登录场景）
    var fromCookie = findStrataJwtFromDocumentCookie();
    if (fromCookie && !isJwtExpired(fromCookie)) {
        return fromCookie;
    }
    // 其次从 sessionStorage 获取（LO 直接登录场景）
    var fromSession = sessionStorage.getItem("ZeitroA");
    if (fromSession && !isJwtExpired(fromSession)) {
        return fromSession;
    }
    return null;
}

/**
 * LO 登录成功后：先清掉旧的 Strata 认证 Cookie，再写入与 Strata AccountAuth 一致的普通租户 Cookie 名，
 * 使打开 www 子域 Strata 时免登，且 Cookie 与 session 为同一 JWT，不再出现「Strata 旧 Cookie 盖掉 LO 新 session」。
 */
export function syncLoTokenToStrataCookies(token) {
    if (!token || isJwtExpired(token)) {
        return;
    }
    clearStrataZeitroAuthCookies();
    var domain = getLoCookieParentDomain();
    setParentDomainJwtCookie(STRATA_JWT_COOKIE_KEY, token, domain);
}

/**
 * 仅当 Cookie 缺失时，用当前 LO session 补写父域 Cookie。
 * 用于 SPA 内部进入 Strata iframe 前，避免 iframe 先加载成未登录。
 */
export function syncLoSessionToStrataCookieIfMissing() {
    if (typeof window === "undefined" || !window.sessionStorage) {
        return false;
    }
    var fromCookie = findStrataJwtFromDocumentCookie();
    if (fromCookie && !isJwtExpired(fromCookie)) {
        return false;
    }
    var token = sessionStorage.getItem("ZeitroA");
    if (!token || isJwtExpired(token)) {
        return false;
    }
    var domain = getLoCookieParentDomain();
    setParentDomainJwtCookie(STRATA_JWT_COOKIE_KEY, token, domain);
    return true;
}

/**
 * 仅清除 Strata 认证 Cookie（登出或替换前使用）。登录成功请优先用 syncLoTokenToStrataCookies。
 */
export function clearStrataZeitroAuthCookies() {
    if (typeof document === "undefined") {
        return;
    }
    var domain = getLoCookieParentDomain();
    var expiry = "expires=Thu, 01 Jan 1970 00:00:00 GMT";
    if (!document.cookie) {
        return;
    }
    var segments = document.cookie.split(";");
    var names = [];
    for (var i = 0; i < segments.length; i++) {
        var segment = segments[i].trim();
        if (!segment) {
            continue;
        }
        var eq = segment.indexOf("=");
        if (eq <= 0) {
            continue;
        }
        var cname = segment.slice(0, eq).trim();
        if (isStrataZeitroAuthCookieName(cname)) {
            names.push(cname);
        }
    }
    for (var j = 0; j < names.length; j++) {
        var name = names[j];
        document.cookie = name + "=;" + expiry + ";path=/;domain=" + domain;
        document.cookie = name + "=;" + expiry + ";path=/";
        try {
            var dec = normalizeCookieNameForClear(name);
            if (dec !== name) {
                document.cookie = dec + "=;" + expiry + ";path=/;domain=" + domain;
                document.cookie = dec + "=;" + expiry + ";path=/";
            }
        } catch (e1) {
            /* ignore */
        }
    }
}

/** JWT exp 已过（含 60s 时钟容差）或无法解析则视为过期，不应再当作已登录 */
function isJwtExpired(token) {
    var skewSec = 60;
    try {
        var p = parseJwtPayload(token);
        if (!p || p.exp == null) {
            return false;
        }
        var now = Math.floor(Date.now() / 1000);
        return Number(p.exp) < now - skewSec;
    } catch (e) {
        return true;
    }
}

export const EMAIL_RELOGIN_PENDING_KEY = "pendingEmailRelogin";

/**
 * 统一处理认证过期（401）：清除 sessionStorage + Cookie。
 * 用于各业务页面收到 401 时调用。跳转循环的防护在 LoginPage 内部处理。
 */
export function handleAuthExpired() {
    clearExpiredLoSessionKeys();
    clearStrataZeitroAuthCookies();
}

function isEmailReloginPending() {
    try {
        return !!sessionStorage.getItem(EMAIL_RELOGIN_PENDING_KEY);
    } catch (e) {
        return false;
    }
}

/**
 * Clear the LO session and send the user to the login page.
 * Read customerid from the JWT before wiping storage so /login/{customerid}
 * still works after an email change or Redis session miss.
 *
 * If an email-change confirm modal is on screen, only clear storage. The
 * modal already captured the login URL and must stay visible.
 */
export function expireAuthAndRedirectToLogin() {
    if (isEmailReloginPending()) {
        handleAuthExpired();
        return;
    }
    var customerId = "";
    try {
        var token = sessionStorage.getItem("ZeitroA");
        var payload = token ? parseJwtPayload(token) : null;
        if (payload && payload.customerid) {
            customerId = String(payload.customerid);
        }
    } catch (e) {}
    handleAuthExpired();
    if (typeof window === "undefined") {
        return;
    }
    if (customerId) {
        window.location.href = "/login/" + encodeURIComponent(customerId);
    } else {
        window.location.href = "/login";
    }
}

function clearExpiredLoSessionKeys() {
    sessionStorage.removeItem("ZeitroA");
    sessionStorage.removeItem("state");
    sessionStorage.removeItem("originalstate");
    sessionStorage.removeItem("path");
}

/**
 * Same as successful /los/signin today: empty State string (Redux then merges initialState in Store).
 * Set synchronously so Store/loadState runs after syncStrataSession see a defined value.
 */
function applyEmptyLoSessionStateLikeSignin() {
    sessionStorage.setItem("state", "");
    sessionStorage.setItem("originalstate", "");
}

/**
 * Match LoginPage after /los/signin: sessionStorage state + originalstate from response State.
 * GET /los/bootstrapsession returns the same authStatus as signin (State is "" for LO today).
 */
function bootstrapLoSessionStateLikeSignin(token) {
    if (!token) {
        return;
    }
    applyEmptyLoSessionStateLikeSignin();
    fetch("/los/bootstrapsession", {
        method: "GET",
        headers: {
            Authorization: "Bearer " + token,
            Cache: "no-cache",
        },
    })
        .then(function (res) {
            if (!res.ok) {
                return Promise.reject(new Error("bootstrapsession " + res.status));
            }
            return res.json();
        })
        .then(function (js) {
            var st = "";
            if (js && js.Status === "OK" && typeof js.State === "string") {
                st = js.State;
            }
            sessionStorage.setItem("state", st);
            sessionStorage.setItem("originalstate", st);
        })
        .catch(function () {
            applyEmptyLoSessionStateLikeSignin();
        });
}

(function syncStrataCookieToLoSession() {
    try {
        if (typeof window === "undefined" || !window.sessionStorage) {
            return;
        }
        var existing = sessionStorage.getItem("ZeitroA");
        if (existing && isJwtExpired(existing)) {
            clearExpiredLoSessionKeys();
            existing = null;
        }
        var fromCookie = findStrataJwtFromDocumentCookie();
        if (fromCookie && isJwtExpired(fromCookie)) {
            fromCookie = null;
        }
        if (!fromCookie) {
            syncLoSessionToStrataCookieIfMissing();
            return;
        }
        existing = sessionStorage.getItem("ZeitroA");
        if (existing === fromCookie) {
            return;
        }
        sessionStorage.setItem("ZeitroA", fromCookie);
        bootstrapLoSessionStateLikeSignin(fromCookie);
    } catch (e) {
        console.warn("syncStrataCookieToLoSession", e);
    }
})();
